import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { setUnauthorizedHandler } from '@/api/client';
import { useAuthStore } from '@/stores/auth';

// 备案极简版：核心页面仅"问答" · 个人中心 + 管理后台经用户头像下拉进入
const routes: RouteRecordRaw[] = [
  // 公开路由
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: '登录 · 理数融合工业大模型', publicRoute: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/RegisterPage.vue'),
    meta: { title: '注册 · 理数融合工业大模型', publicRoute: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { title: '找回密码 · 理数融合工业大模型', publicRoute: true },
  },

  // 首页 (公开) · 不强制重定向 · 登录与否都可看到宣传页 + CTA
  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/LandingPage.vue'),
    meta: { title: '理数融合工业大模型 · 机理模型 × 数据模型 深度融合', publicRoute: true },
  },

  // 认证后 Shell · 默认直达对话
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      { path: '', redirect: '/app/chat' },
      { path: 'chat', name: 'chat', component: () => import('@/pages/ChatPage.vue'), meta: { title: '问答 · 理数融合工业大模型' } },
      { path: 'chat/:conversationId', component: () => import('@/pages/ChatPage.vue'), meta: { title: '问答 · 理数融合工业大模型' } },

      // 个人中心（经用户头像下拉进入）
      { path: 'me', redirect: '/app/me/profile' },
      { path: 'me/profile', component: () => import('@/pages/ProfilePage.vue'), meta: { title: '个人信息' } },
      { path: 'me/agreements', component: () => import('@/pages/AgreementsHubPage.vue'), meta: { title: '协议与隐私' } },
      { path: 'me/violations', component: () => import('@/pages/ViolationsPage.vue'), meta: { title: '警告历史' } },
      { path: 'me/notifications', component: () => import('@/pages/NotificationsPage.vue'), meta: { title: '通知中心' } },
      { path: 'me/data-rights', redirect: '/app/me/profile' },
      { path: 'me/complaints', component: () => import('@/pages/ComplaintsRealPage.vue'), meta: { title: '我的投诉举报' } },
      { path: 'me/complaints/:id', component: () => import('@/pages/ComplaintDetailPage.vue'), meta: { title: '投诉工单详情' } },

      // 协议全文（底栏链接）
      { path: 'agreements/privacy', component: () => import('@/pages/AgreementsHubPage.vue'), meta: { title: '用户隐私政策' } },
      { path: 'agreements/service', component: () => import('@/pages/AgreementsHubPage.vue'), meta: { title: '用户服务协议' } },

      // 管理员 Shell · 左侧导航
      {
        path: 'admin',
        component: () => import('@/layouts/AdminLayout.vue'),
        meta: { adminOnly: true },
        children: [
          { path: '', component: () => import('@/pages/AdminDashboardPage.vue'), meta: { title: '安全大盘 · 管理员', adminOnly: true } },
          { path: 'users', component: () => import('@/pages/AdminUsersPage.vue'), meta: { title: '用户管理 · 管理员', adminOnly: true } },
          { path: 'sensitive-words', component: () => import('@/pages/AdminSensitiveWordsPage.vue'), meta: { title: '敏感词库 · 管理员', adminOnly: true } },
          { path: 'safety-alerts', component: () => import('@/pages/AdminCriticalAlertsPage.vue'), meta: { title: '严重告警 · 管理员', adminOnly: true } },
          { path: 'alerts', component: () => import('@/pages/AdminAlertsPage.vue'), meta: { title: '告警阈值 · 管理员', adminOnly: true } },
          { path: 'audit', component: () => import('@/pages/AdminAuditPage.vue'), meta: { title: '审计日志 · 管理员', adminOnly: true } },
          { path: 'agreements', component: () => import('@/pages/AdminAgreementsPage.vue'), meta: { title: '协议管理 · 管理员', adminOnly: true } },
          { path: 'test-accounts', component: () => import('@/pages/AdminTestAccountsPage.vue'), meta: { title: '测试账号 · 管理员', adminOnly: true } },
          { path: 'complaints', component: () => import('@/pages/AdminComplaintsPage.vue'), meta: { title: '投诉处置 · 管理员', adminOnly: true } },
        ],
      },
    ],
  },

  // 兼容旧路径
  { path: '/welcome', redirect: '/app/chat' },
  { path: '/chat', redirect: '/app/chat' },
  // 已下线页面的兜底 → 回对话首页
  { path: '/app/welcome', redirect: '/app/chat' },
  { path: '/app/literature', redirect: '/app/chat' },
  { path: '/app/literature/:id', redirect: '/app/chat' },
  { path: '/app/ingestion', redirect: '/app/chat' },
  { path: '/app/knowledge', redirect: '/app/chat' },
  { path: '/app/graph', redirect: '/app/chat' },
  { path: '/app/complaints', redirect: '/app/me/complaints' },
  { path: '/app/complaints/:id', redirect: (to) => `/app/me/complaints/${to.params.id}` },
  { path: '/app/support', redirect: '/app/chat' },
  { path: '/app/feedback', redirect: '/app/chat' },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { publicRoute: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (!to.meta.publicRoute && !to.path.startsWith('/login') && !to.path.startsWith('/register') && !to.path.startsWith('/forgot-password')) {
    if (!auth.isLoggedIn) {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
  }
  if ((to.name === 'login' || to.name === 'register') && auth.isLoggedIn) {
    return '/app/chat';
  }
  // 管理员准入：userType=regulator_test (测试账号) OR role=super_admin / safety_ops (RBAC)
  if (to.meta.adminOnly) {
    const isAdmin = auth.userType === 'regulator_test'
      || auth.role === 'super_admin'
      || auth.role === 'safety_ops';
    if (!isAdmin) return '/app/chat';
  }
  return true;
});

router.afterEach((to) => {
  if (typeof to.meta.title === 'string') {
    document.title = to.meta.title;
  }
});

setUnauthorizedHandler(() => {
  const auth = useAuthStore();
  auth.logout();
  if (router.currentRoute.value.name !== 'login') {
    router.push({ name: 'login' });
  }
});
